/*
 * ROLES-NOTIFY01 (ТЗ v4 §29, FE-FIX26): декодирование read model «Роли →
 * уведомления о событиях».
 *
 * Ответ backend не типизирован в openapi, поэтому имена полей приводятся здесь
 * к одной форме — ровно так же, как это уже делает CameraNotificationGroups с
 * каталогом событий. Важно, что декодируется только представление: состояние
 * канала берётся из ответа, никаких «посчитаем сами по камерам» нет.
 *
 * Состояние канала трёхзначное:
 *   on    — включено на всех доступных камерах (глобальное желаемое состояние);
 *   off   — выключено везде;
 *   mixed — есть camera-level override, то есть часть камер отличается;
 *   unknown — backend не прислал состояние; выдумывать «выключено» нельзя.
 *
 * mixed — не ошибка: camera-level правка остаётся авторитетным override, и
 * массовое применение её сознательно перекрывает.
 */

import { countRu, CAMERA_FORMS } from './pluralRu';

export const CHANNEL_STATE = {
  on: 'on',
  off: 'off',
  mixed: 'mixed',
  unknown: 'unknown',
};

export const CHANNEL_STATE_LABEL = {
  on: 'Включено',
  off: 'Выключено',
  mixed: 'Частично (есть переопределения)',
  unknown: 'Нет данных',
};

export const CHANNELS = [
  { key: 'email', label: 'Email', param: 'email_enabled' },
  { key: 'webPush', label: 'WEB PUSH', param: 'web_push_enabled' },
];

const firstDefined = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
};

const asList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.users)) return data.users;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const STATE_WORDS = {
  on: CHANNEL_STATE.on,
  enabled: CHANNEL_STATE.on,
  all: CHANNEL_STATE.on,
  true: CHANNEL_STATE.on,
  off: CHANNEL_STATE.off,
  disabled: CHANNEL_STATE.off,
  none: CHANNEL_STATE.off,
  false: CHANNEL_STATE.off,
  mixed: CHANNEL_STATE.mixed,
  partial: CHANNEL_STATE.mixed,
  override: CHANNEL_STATE.mixed,
  overridden: CHANNEL_STATE.mixed,
};

// Камера в ответе несёт и свой camera_id, и location_id своей локации, поэтому
// порядок ключей у каждого вида сущности свой.
const ENTITY_KEYS = {
  location: {
    id: ['id', 'location_id', 'room_id', 'key'],
    name: ['name', 'display_name', 'location_name', 'title'],
  },
  camera: {
    id: ['camera_id', 'id', 'key'],
    name: ['camera_name', 'name', 'display_name', 'title'],
  },
};

/**
 * Цепочка «объект → подразделение → область → локация» из access-строки.
 * Нужна, чтобы ссылка из «Ролей» открывала «Камеры» сразу на этой локации, а
 * не на корне иерархии. Локация сама является `room`, у камеры локация —
 * `location_*`. Неполная цепочка → null: угадывать недостающие уровни нельзя.
 */
const hierarchyPath = (item, kind, id, name) => {
  const level = (idKey, nameKey) => {
    const value = item?.[idKey];
    return value == null ? null : { id: value, name: item?.[nameKey] || '' };
  };
  const path = {
    factory: level('factory_id', 'factory_name'),
    workshop: level('workshop_id', 'workshop_name'),
    section: level('section_id', 'section_name'),
    room: kind === 'location'
      ? (id === undefined ? null : { id, name: name || '' })
      : level('location_id', 'location_name'),
  };
  return Object.values(path).every(Boolean) ? path : null;
};

const entity = (item, kind, fallbackPrefix) => {
  const id = firstDefined(item, ENTITY_KEYS[kind].id);
  const name = firstDefined(item, ENTITY_KEYS[kind].name);
  if (id === undefined && name === undefined) return null;
  return {
    id: id === undefined ? '' : String(id),
    name: name === undefined ? `${fallbackPrefix}${id}` : String(name),
    path: hierarchyPath(item, kind, id, name),
  };
};

const entities = (source, keys, kind, fallbackPrefix) => {
  const raw = firstDefined(source, keys);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => (typeof item === 'object' ? entity(item, kind, fallbackPrefix) : { id: String(item), name: `${fallbackPrefix}${item}` }))
    .filter(Boolean);
};

/**
 * Состояние одного канала. Порядок попыток — от самого явного к самому
 * косвенному, и любой признак «часть камер отличается» сильнее булева флага.
 */
export const resolveChannelState = (source, channelKey) => {
  if (!source || typeof source !== 'object') return CHANNEL_STATE.unknown;

  const nested = source[channelKey] || source[channelKey === 'webPush' ? 'web_push' : channelKey];
  if (nested && typeof nested === 'object') {
    const inner = resolveChannelState(nested, channelKey);
    if (inner !== CHANNEL_STATE.unknown) return inner;
  }

  const prefix = channelKey === 'webPush' ? 'web_push' : 'email';
  const stateWord = firstDefined(source, [`${prefix}_state`, 'state', 'status', 'value']);
  if (typeof stateWord === 'string') {
    const mapped = STATE_WORDS[stateWord.toLowerCase()];
    if (mapped) return mapped;
  }

  const mixedFlag = firstDefined(source, [
    `${prefix}_mixed`, `${prefix}_override`, `${prefix}_has_override`, `${prefix}_mismatch`,
    'mixed', 'has_override', 'override', 'mismatch',
  ]);
  if (mixedFlag === true) return CHANNEL_STATE.mixed;

  const enabledCount = firstDefined(source, [`${prefix}_enabled_count`, 'enabled_count']);
  const total = firstDefined(source, [`${prefix}_camera_count`, 'camera_count', 'total', 'cameras_count']);
  if (typeof enabledCount === 'number' && typeof total === 'number' && total > 0) {
    if (enabledCount === 0) return CHANNEL_STATE.off;
    if (enabledCount >= total) return CHANNEL_STATE.on;
    return CHANNEL_STATE.mixed;
  }

  const flag = firstDefined(source, [`${prefix}_enabled`, 'enabled']);
  if (typeof flag === 'boolean') return flag ? CHANNEL_STATE.on : CHANNEL_STATE.off;

  return CHANNEL_STATE.unknown;
};

const isAdminUser = (item) => {
  const flag = firstDefined(item, ['is_admin', 'admin']);
  if (typeof flag === 'boolean') return flag;
  const role = firstDefined(item, ['role', 'user_role']);
  return typeof role === 'string' && role.toLowerCase() === 'admin';
};

const displayName = (user) => {
  const full = [user?.name, user?.surname].filter(Boolean).join(' ').trim();
  return full || firstDefined(user, ['display_name', 'username']);
};

/**
 * Строка таблицы «Роли». Backend отдаёт `{ user, access, event_notifications }`:
 * учётка — в `user`, доступные локации/камеры и счётчики — в `access`. Плоская
 * форма тоже принимается. Админ по правилу представления уезжает в конец
 * списка — это только сортировка, модель безопасности backend не меняется.
 */
export const normalizeRoleNotificationUsers = (data) => {
  const channelsSource = (item) => firstDefined(item, ['event_notifications', 'notifications', 'channels']) || item;

  const rows = asList(data).map((item) => {
    const user = item?.user && typeof item.user === 'object' ? { ...item, ...item.user } : item;
    const access = item?.access && typeof item.access === 'object' ? item.access : item;
    const source = channelsSource(item);
    const locations = entities(access, ['locations', 'accessible_locations', 'rooms'], 'location', 'Локация #');
    const cameras = entities(access, ['cameras', 'accessible_cameras'], 'camera', 'Камера #');
    const id = firstDefined(user, ['user_id', 'id', 'uuid', 'email']);
    const email = firstDefined(user, ['email', 'mail']) || '';
    return {
      id: id === undefined ? '' : String(id),
      email,
      name: displayName(user) || (email ? email.split('@')[0] : String(id ?? '')),
      isAdmin: isAdminUser(user),
      locations,
      cameras,
      locationCount: firstDefined(access, ['location_count', 'locations_count']) ?? locations.length,
      cameraCount: firstDefined(access, ['camera_count', 'cameras_count']) ?? cameras.length,
      channels: {
        email: resolveChannelState(source, 'email'),
        webPush: resolveChannelState(source, 'webPush'),
      },
      navigation: {
        visibleKeys: firstDefined(item, ['navigation_visible_keys', 'visible_keys', 'visible_tabs']) || null,
        version: firstDefined(item, ['navigation_version', 'version']) ?? null,
        total: firstDefined(item, ['navigation_total', 'navigation_keys_total']) ?? null,
      },
    };
  });

  return rows.sort((a, b) => {
    if (a.isAdmin !== b.isAdmin) return a.isAdmin ? 1 : -1;
    return String(a.name).localeCompare(String(b.name), 'ru', { sensitivity: 'base' });
  });
};

/**
 * Staged-изменения: пользователь ставит желаемое состояние каналов, и до
 * «Подтвердить» ничего не отправляется. Здесь же собираются запросы — по одному
 * bulk на направление (включить/выключить), а не по запросу на камеру.
 */
export const buildBulkRequests = (staged, currentChannels) => {
  const targets = { true: [], false: [] };
  CHANNELS.forEach(({ key, param }) => {
    const desired = staged?.[key];
    if (typeof desired !== 'boolean') return;
    const current = currentChannels?.[key];
    // mixed и unknown всегда отправляем: мы не знаем, что именно переопределено.
    if ((desired && current === CHANNEL_STATE.on) || (!desired && current === CHANNEL_STATE.off)) return;
    targets[String(desired)].push(param);
  });

  return [true, false]
    .filter((enabled) => targets[String(enabled)].length > 0)
    .map((enabled) => ({
      enabled,
      confirm: true,
      // ТЗ V5 №9: в запросе только канал, который меняется. Второй канал не
      // передаётся вовсе — иначе его явное значение задевает чужую настройку.
      ...Object.fromEntries(targets[String(enabled)].map((param) => [param, true])),
    }));
};

export const hasStagedChanges = (staged, currentChannels) => buildBulkRequests(staged, currentChannels).length > 0;

/** Счётчики bulk-ответа: «уже в этом состоянии» — не ошибка (идемпотентность). */
export const describeBulkResult = (result) => {
  const affected = firstDefined(result, ['affected', 'affected_cameras', 'total']);
  const changed = firstDefined(result, ['changed', 'changed_cameras', 'updated']);
  const already = firstDefined(result, ['already', 'already_cameras', 'unchanged', 'already_in_state']);
  // Без счётчиков в ответе числа не выдумываем.
  if (affected === undefined && changed === undefined && already === undefined) return '';
  return `Изменено ${changed ?? 0} из ${affected ?? 0} · ${already ?? 0} уже в нужном состоянии`;
};

/** «Email включён», «WEB PUSH выключен», «Email и WEB PUSH включены» — по флагам запроса. */
export const describeBulkRequest = (request) => {
  const names = CHANNELS.filter(({ param }) => request?.[param] === true).map(({ label }) => label);
  if (names.length === 0) return '';
  const several = names.length > 1;
  const verb = request?.enabled
    ? (several ? 'включены' : 'включён')
    : (several ? 'выключены' : 'выключен');
  return `${names.join(' и ')} ${verb}`;
};

const problemText = (item) => {
  const text = typeof item === 'string' ? item : firstDefined(item, ['message', 'detail', 'error', 'reason']);
  // Русский текст backend показываем как есть, технический код — нет.
  if (typeof text !== 'string' || !/[А-Яа-яЁё]/.test(text) || /[A-Za-z0-9]+_[A-Za-z0-9_]+/.test(text)) {
    return 'Не удалось подтвердить доставку';
  }
  return text;
};

/**
 * Ошибки подтверждения и доставки из ответа bulk. Успешный HTTP не значит, что
 * всё доставлено: ошибки показываем, а постановку в очередь не выдаём за доставку.
 */
export const describeBulkProblems = (result) => {
  if (!result || typeof result !== 'object') return [];
  const problems = [];
  ['errors', 'delivery_errors', 'confirmation_errors'].forEach((key) => {
    if (Array.isArray(result[key])) result[key].forEach((item) => problems.push(problemText(item)));
  });
  const failed = firstDefined(result, ['failed', 'failed_cameras', 'failed_count']);
  if (typeof failed === 'number' && failed > 0) problems.push(`Не изменено ${countRu(failed, CAMERA_FORMS)}`);
  const queued = firstDefined(result, ['queued', 'queued_count']);
  if (typeof queued === 'number' && queued > 0) {
    problems.push('Отправка поставлена в очередь, доставка пока не подтверждена');
  }
  return problems;
};
